import type { ID, ResourceResponse, ThunkReturnType } from "types";
import type { PayloadAction, Draft, WritableDraft } from "infrastructure/store";
import { ApiState } from "infrastructure/api";
import { SetupIdentifier } from "consts/setups";
import { compareIds } from "util/support";
import type {
    SetupsState,
    SetupsResponse,
    SetupResponse,
    Setup,
    SetupsBootstrapEntity
} from "./types";

export const setupsRequestLoading = (state: WritableDraft<SetupsState>) => {
    state.tableLoadingState = ApiState.Pending;
};

export const setupByIdRequestLoading = (
    state: WritableDraft<SetupsState>,
    { meta }: PayloadAction<
        Draft<undefined>,
        string,
        { readonly arg: Setup<string>['identifier']; }
    >
) => {
    state[meta.arg as SetupsBootstrapEntity].loadingState = ApiState.Pending;
};

export const exportSetupsRequestLoading = (
    state: WritableDraft<SetupsState>,
    { payload }: PayloadAction<Draft<ApiState>>
) => {
    state.exportLoadingState = payload;
};

export const setupsRequestFulfilled = (
    state: WritableDraft<SetupsState>,
    { payload }: PayloadAction<ThunkReturnType<SetupsResponse>>
) => {
    state.tableLoadingState = ApiState.Succeeded;
    state.tableSlice = payload!.data;

    payload!.data.data.forEach((setup: Setup<string>) => {
        const identifier = setup.identifier as SetupsBootstrapEntity;

        if (identifier in state) {
            state[identifier].loadingState = ApiState.Succeeded;
            state[identifier].slice = setup as SetupResponse<SetupIdentifier>['data'];
        }
    });
};

export const setupByIdRequestFulfilled = (
    state: WritableDraft<SetupsState>,
    {
        payload,
        meta
    }: PayloadAction<
        ThunkReturnType<SetupResponse<string>>,
        string,
        { readonly arg: Setup<string>['identifier']; }
    >
) => {
    const identifier = meta.arg as SetupsBootstrapEntity;

    state[identifier].loadingState = ApiState.Succeeded;
    state[identifier].slice = payload!.data as SetupResponse<SetupIdentifier>['data'];
};

export const setupsRequestRejected = (state: WritableDraft<SetupsState>) => {
    state.tableLoadingState = ApiState.Failed;
};

export const setupByIdRequestRejected = (
    state: WritableDraft<SetupsState>,
    { meta }: PayloadAction<
        Draft<unknown>,
        string,
        { readonly arg: Setup<string>['identifier']; }
    >
) => {
    state[meta.arg as SetupsBootstrapEntity].loadingState = ApiState.Failed;
};

export const updateOrCreateSetupRequestFulfilled = (
    state: WritableDraft<SetupsState>,
    { payload }: PayloadAction<ThunkReturnType<SetupResponse<string>>>
) => {
    state.tableLoadingState = ApiState.Succeeded;

    if (!state.tableSlice || !payload) {
        return;
    }

    let isUpdated = false;

    state.tableSlice.data = state.tableSlice.data.map((setup: Setup<string>) => {
        if (compareIds(setup.coreId, payload.data.coreId)) {
            isUpdated = true;

            return ({
                ...setup,
                ...payload
            });
        }

        return setup;
    });

    if (isUpdated) {
        const identifier = payload.data.identifier as SetupsBootstrapEntity;

        if (identifier in state) {
            state[identifier].loadingState = ApiState.Succeeded;
            state[identifier].slice = payload.data as SetupResponse<SetupIdentifier>['data'];
        }
        return;
    }

    state.tableSlice.data.unshift(payload.data);
    state.tableSlice!.to += 1;
    state.tableSlice!.total += 1;
};

export const deleteSetupRequestFulfilled = (
    state: WritableDraft<SetupsState>,
    { payload }: PayloadAction<ThunkReturnType<ResourceResponse<ID>>>
) => {
    state.tableLoadingState = ApiState.Succeeded;

    if (!state.tableSlice || !payload) {
        return;
    }

    state.tableSlice.data = state.tableSlice.data.filter(({ coreId, identifier }) => {
        const identifierProperty = identifier as SetupsBootstrapEntity;
        const shouldRemove = compareIds(coreId, payload.data);

        if (shouldRemove && (identifierProperty in state)) {
            state[identifierProperty].slice = null;
        }

        return !shouldRemove;
    });

    state.tableSlice.to -= 1;
    state.tableSlice.total -= 1;
};